import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { adjusters } from '@/data/MoAdjusters';
import { Pagination } from 'antd';

const Adjusters = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);

  const keyword = params.get('keyword')?.trim() || '';
  const filter = params.get('filter') || 'all';

  const initialPage = parseInt(params.get('page') || '1', 10);
  const [page, setPage] = useState(initialPage);
  const pageSize = 4;

  useEffect(() => {
    // 페이지 바뀌면 URL도 업데이트
    navigate(`?filter=${filter}&keyword=${keyword}&page=${page}`, { replace: true });
  }, [page]);
  //손해사정사 필터
  const filteredAdjusters = useMemo(() => {
    if (!keyword) return adjusters;

    if (filter === 'name') {
      return adjusters.filter((a) => a.name.includes(keyword));
    } else if (filter === 'detail') {
      return adjusters.filter((a) => a.detail.includes(keyword));
    } else {
      return adjusters.filter((a) => a.name.includes(keyword) || a.detail.includes(keyword));
    }
  }, [keyword, filter]);

  const totalPages = Math.ceil(filteredAdjusters.length / pageSize);
  const totalItems = filteredAdjusters.length;
  const pagedAdjusters = filteredAdjusters.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <Wrapper>
        {pagedAdjusters.map((adjuster) => (
          <NewsCard key={adjuster.id}>
            <ImageBox onClick={() => navigate(`/intro/adjusters/${adjuster.id}`)}>
              <StyledImage src={adjuster.image} alt={adjuster.name} />
            </ImageBox>
            <ContentBox>
              <InfoGroup>
                <InfoTop>
                  <Label>{adjuster.label}</Label>
                  <NameRow>
                    <Name>{adjuster.name}</Name>
                    <RegionTag>
                      <RegionText>{adjuster.region}</RegionText>
                    </RegionTag>
                  </NameRow>
                  <Detail>{adjuster.detail}</Detail>
                </InfoTop>
              </InfoGroup>
              <ViewButton onClick={() => navigate(`/intro/adjusters/${adjuster.id}`)}>
                <ViewButtonInner>자세히 보기</ViewButtonInner>
              </ViewButton>
            </ContentBox>
          </NewsCard>
        ))}
      </Wrapper>

      <PaginationWrapper>
        {totalPages > 1 && (
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalItems}
            onChange={(newPage) => setPage(newPage)}
            showSizeChanger={false} // 페이지 크기 선택 비활성화
          />
        )}
      </PaginationWrapper>
    </>
  );
};

export default Adjusters;

const Wrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  width: 100%;
`;

const NewsCard = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  align-self: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const ImageBox = styled.div`
  width: 200px;
  height: 200px;
  background-color: #eee;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;

    img {
      width: 100%;
      height: auto;
    }
  }
`;

const StyledImage = styled.img`
  width: 100%;
  object-fit: cover;
  cursor: pointer;
`;

const ContentBox = styled.div`
  display: flex;
  width: 374px;
  padding: 16px 0;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const InfoTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Label = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray04, #6f6f6f);
  font-size: 16px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.32px;
  text-align: start;
  cursor: text;
  margin-bottom: 16px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  margin-bottom: 16px;
`;

const Name = styled.div`
  color: var(--gray-ui_gray02, #252525);
  font-size: 26px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.52px;
  cursor: text;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const RegionTag = styled.div`
  display: flex;
  padding: 0px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 99px;
  background: var(--Primary-lightgray, #f3f3f3);
`;

const RegionText = styled.div`
  color: var(--Primary-darkgray, #000c24);
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
  cursor: text;
`;

const Detail = styled.div`
  text-align: start;
  align-self: stretch;
  color: var(--gray-ui_gray05, #999);
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
  cursor: text;
  margin-bottom: 16px;
`;

const ViewButton = styled.button`
  display: flex;
  height: 40px;
  padding: 16px 12px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: var(--table-BG-table_bg02_hov, #f0f3fe);
  border: none;
  cursor: pointer;

  &:hover div {
    color: #4c84e3;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ViewButtonInner = styled.div`
  display: flex;
  padding: 0px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: #3a3a3a;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const PaginationWrapper = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  min-height: 40px;
  display: flex;
  justify-content: center;
`;
